class ScrollSnapSlider extends HTMLUListElement {
  constructor() {
    super();

    this.prevButton = this.parentNode.querySelector(".slider-buttons .slider-button--prev");
    this.nextButton = this.parentNode.querySelector(".slider-buttons .slider-button--next");
    this.indicators = this.parentNode.querySelectorAll(".indicator");
    this.currentIndicator = this.parentNode.querySelector(".indicator .current-indicator");
    this.isAutoplay = (this.dataset.autoplay || "false") === 'true';
    this.isLoop = (this.dataset.loop || "false") === 'true';
    this.isDraggable = (this.dataset.draggable || "false") === 'true';
    this.isInfinite = (this.dataset.infinite || "false") === 'true';
    this.autoplayInterval = this.dataset.autoplayInterval ? this.dataset.autoplayInterval : 3000;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  async init() {
    // Initialize infinite scroll before the slider plugin
    if (this.isInfinite) {
      this.initInfiniteScroll();
    }

    const { ScrollSnapSlider, ScrollSnapDraggable, ScrollSnapAutoplay, ScrollSnapLoop } = await import('mdl-scrollsnap');

    const sliderElement = this;
    const sliderSimple = new ScrollSnapSlider({ element: this });
    const draggablePlugin = new ScrollSnapDraggable(70);
    const autoplayPlugin = new ScrollSnapAutoplay(this.autoplayInterval);
    const loopPlugin = new ScrollSnapLoop();

    this.slider = sliderSimple;

    draggablePlugin.slider = sliderSimple;
    autoplayPlugin.slider = sliderSimple;
    loopPlugin.slider = sliderSimple;

    this.prevButton?.addEventListener("click", () => {
      autoplayPlugin.disableTemporarily();
      sliderSimple.slideTo(sliderSimple.slide - 1);
    });
    this.nextButton?.addEventListener("click", () => {
      autoplayPlugin.disableTemporarily();
      sliderSimple.slideTo(sliderSimple.slide + 1);
    });

    for (const indicator of this.indicators) {
      indicator.addEventListener("click", function () {
        autoplayPlugin.disableTemporarily();
        const slideElementIndex = Array.prototype.slice.call(sliderElement.children).findIndex(item => item.dataset.index === indicator.dataset.index);
        sliderSimple.slideTo(slideElementIndex);
      });
    }

    this.slider.addEventListener("slide-stop", (e) => {
      this.updateSlideState(e);
      this.updateIndicator(e);
      // Handle infinite scroll repositioning
      if (this.isInfinite) {
        this.handleInfiniteReposition();
      }
    });

    // For infinite scroll, never disable buttons
    if (this.isInfinite) {
      this.prevButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
    } else if (!this.isLoop || this.isDraggable) {
      this.slider.addEventListener("slide-stop", () => {
        this.prevButton?.removeAttribute('disabled');
        this.nextButton?.removeAttribute('disabled');
        if (sliderSimple.firstItemInView) {
          this.prevButton?.setAttribute('disabled', 'disabled');
        }
        if (sliderSimple.lastItemInView) {
          this.nextButton?.setAttribute('disabled', 'disabled');
        }
      });
    }

    if (this.isAutoplay) {
      this.enablePlugin(autoplayPlugin);
    }

    if (this.isLoop && !this.isInfinite) {
      this.enablePlugin(loopPlugin);
    }

    if (this.isDraggable) {
      this.enablePlugin(draggablePlugin);
      this.initDragClickPrevention();
      if (this.isLoop && !this.isInfinite) {
        this.disablePlugin(loopPlugin);
      }
      return;
    }
    this.disablePlugin(draggablePlugin);
  }

  initDragClickPrevention() {
    let startX = 0;
    let startY = 0;
    let hasDragged = false;
    const dragThreshold = 8; // pixels moved before considered a drag

    this.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
      hasDragged = false;
    });

    this.addEventListener('mousemove', (e) => {
      if (e.buttons === 1) { // Left mouse button is pressed
        const deltaX = Math.abs(e.clientX - startX);
        const deltaY = Math.abs(e.clientY - startY);
        if (deltaX > dragThreshold || deltaY > dragThreshold) {
          if (!hasDragged) {
            hasDragged = true;
            this.classList.add('is-dragging');
          }
        }
      }
    });

    // Remove dragging class on mouseup
    document.addEventListener('mouseup', () => {
      this.classList.remove('is-dragging');
    });

    // Capture click events and prevent if dragged
    this.addEventListener('click', (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    }, true); // Use capture phase to intercept before bubbling

    // Also handle touch for mobile
    let touchStartX = 0;
    let touchStartY = 0;
    let hasTouchDragged = false;

    this.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        hasTouchDragged = false;
      }
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      if (e.touches.length === 1) {
        const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
        const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
        if (deltaX > dragThreshold || deltaY > dragThreshold) {
          hasTouchDragged = true;
        }
      }
    }, { passive: true });

    this.addEventListener('click', (e) => {
      if (hasTouchDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasTouchDragged = false;
      }
    }, true);
  }

  initInfiniteScroll() {
    // Store original items
    const originalItems = Array.from(this.children);
    this.originalItemCount = originalItems.length;

    if (this.originalItemCount < 2) return;

    // Clone items and add to start and end
    const clonesBefore = [];
    const clonesAfter = [];

    originalItems.forEach((item, index) => {
      const cloneBefore = item.cloneNode(true);
      const cloneAfter = item.cloneNode(true);

      // Mark clones and update IDs to avoid duplicates
      cloneBefore.classList.add('infinite-clone', 'clone-before');
      cloneAfter.classList.add('infinite-clone', 'clone-after');
      cloneBefore.id = cloneBefore.id ? `${cloneBefore.id}-clone-before` : '';
      cloneAfter.id = cloneAfter.id ? `${cloneAfter.id}-clone-after` : '';

      clonesBefore.push(cloneBefore);
      clonesAfter.push(cloneAfter);
    });

    // Insert clones: [clones] [original] [clones]
    clonesBefore.reverse().forEach(clone => {
      this.insertBefore(clone, this.firstChild);
    });
    clonesAfter.forEach(clone => {
      this.appendChild(clone);
    });

    // Set initial scroll position to first original item (after a brief delay for layout)
    requestAnimationFrame(() => {
      const firstOriginal = this.children[this.originalItemCount];
      if (firstOriginal) {
        this.scrollLeft = firstOriginal.offsetLeft;
      }
    });
  }

  handleInfiniteReposition() {
    if (!this.originalItemCount) return;

    const currentSlide = this.slider.slide;
    const totalSlides = this.children.length;
    const clonesCount = this.originalItemCount;

    // If we're in the "before" clones section, jump to corresponding original
    if (currentSlide < clonesCount) {
      const targetSlide = currentSlide + clonesCount;
      this.repositionToSlide(targetSlide);
    }
    // If we're in the "after" clones section, jump to corresponding original
    else if (currentSlide >= totalSlides - clonesCount) {
      const targetSlide = currentSlide - clonesCount;
      this.repositionToSlide(targetSlide);
    }
  }

  repositionToSlide(targetSlide) {
    // Disable smooth scrolling temporarily
    const originalBehavior = this.style.scrollBehavior;
    this.style.scrollBehavior = 'auto';

    // Jump to the target slide
    const targetElement = this.children[targetSlide];
    if (targetElement) {
      this.scrollLeft = targetElement.offsetLeft;
      // Update the slider's internal state
      if (this.slider) {
        this.slider.slide = targetSlide;
      }
    }

    // Re-enable smooth scrolling
    requestAnimationFrame(() => {
      this.style.scrollBehavior = originalBehavior || '';
    });
  }

  enablePlugin(plugin) {
    plugin.enable();
    this.slider.plugins.set(plugin.id, plugin);
  }

  disablePlugin(plugin) {
    plugin.disable();
    this.slider.plugins.delete(plugin.id);
  }

  updateSlideState(event) {
    const children = event.target.children; 
      if (!children || event.detail < 0 || event.detail >= children.length) {
        return; 
      }
      for (let child of children) {
        child.classList.remove('-active');
      }
      children[event.detail].classList.add('-active');
  }

  updateIndicator(event) {
    const slideElementIndex = event.detail;
    const slideElement = event.target.children[slideElementIndex];

    for (const indicator of this.indicators) {
      const isActive = indicator.classList.toggle(
      "-active",
      indicator.dataset.index === slideElement.dataset.index);
  
      if (isActive) {
        indicator.appendChild(this.currentIndicator);
      }
    }
  }
}
customElements.define('scroll-snap-slider', ScrollSnapSlider, { extends: 'ul' });